import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import { logout as authLogout } from "../../services/dataService";
import { useToast } from "../../contexts/ToastContext";
import Sidebar, { getSidebarMenuAction } from "../Sidebar";
import WorkspaceHeader from "../WorkspaceHeader";
import {
  buildReceiptDateRange,
  createTodayReceiptFilters,
  formatReceiptDateTime,
  isReceiptEditableToday,
} from "../../utils/receiptHistory";
import ReceiptDetailDialog from "./ReceiptDetailDialog";
import ReceiptCorrectionDialog from "./ReceiptCorrectionDialog";

const formatCurrency = (value) =>
  Math.round(Number(value) || 0).toLocaleString("en-US");

const errorMessage = (error, fallback) =>
  error?.response?.data?.error || fallback;

const RECEIPTS_PAGE_SIZE = 50;

const fetchReceiptPage = (filters, before = null) => {
  const range = buildReceiptDateRange(filters);
  return api.get("/receipts", {
    from: range.from,
    to: range.to,
    productSearch: filters.productSearch,
    limit: RECEIPTS_PAGE_SIZE,
    ...(before ? { before } : {}),
  });
};

export default function ReceiptsView() {
  const navigate = useNavigate();
  const showToast = useToast();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const mobileMenuButtonRef = useRef(null);
  const [filters, setFilters] = useState(() => createTodayReceiptFilters());
  const [receipts, setReceipts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCursors, setPageCursors] = useState([null]);
  const [loading, setLoading] = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [editingReceipt, setEditingReceipt] = useState(null);
  const [deletingTransactionId, setDeletingTransactionId] = useState(null);
  const { dateFrom, dateTo, timeFrom, timeTo, productSearch } = filters;

  useEffect(() => {
    try {
      setUser(JSON.parse(localStorage.getItem("user") || "null"));
    } catch {
      setUser(null);
    }
  }, []);

  const handleOpenMenu = () => {
    const action = getSidebarMenuAction(window.innerWidth, sidebarCollapsed);
    if (action === "open") {
      setSidebarOpen(true);
    } else if (action === "expand") {
      setSidebarCollapsed(false);
    }
  };

  const handleLogout = async () => {
    await authLogout();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    let active = true;
    const receiptFilters = {
      dateFrom,
      dateTo,
      timeFrom,
      timeTo,
      productSearch,
    };
    setLoading(true);
    setNextCursor(null);
    setPageIndex(0);
    setPageCursors([null]);

    fetchReceiptPage(receiptFilters)
      .then((response) => {
        if (!active) return;
        setReceipts(response.data?.receipts || []);
        setNextCursor(response.data?.nextCursor || null);
      })
      .catch((error) => {
        if (active) {
          setReceipts([]);
          showToast(errorMessage(error, "Receipts could not be loaded."), "error");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [
    dateFrom,
    dateTo,
    timeFrom,
    timeTo,
    productSearch,
    showToast,
  ]);

  const updateFilter = (event) => {
    const { name, value } = event.target;
    setFilters((current) => ({ ...current, [name]: value }));
  };

  const clearFilters = () => setFilters(createTodayReceiptFilters());

  const goToNextPage = async () => {
    if (!nextCursor || loadingPage) return;
    setLoadingPage(true);
    try {
      const response = await fetchReceiptPage(filters, nextCursor);
      setReceipts(response.data?.receipts || []);
      setNextCursor(response.data?.nextCursor || null);
      setPageCursors((current) => [...current, nextCursor]);
      setPageIndex((current) => current + 1);
    } catch (error) {
      showToast(errorMessage(error, "The next receipt page could not be loaded."), "error");
    } finally {
      setLoadingPage(false);
    }
  };

  const goToPreviousPage = async () => {
    if (pageIndex === 0 || loadingPage) return;
    const before = pageCursors[pageIndex - 1] || null;
    setLoadingPage(true);
    try {
      const response = await fetchReceiptPage(filters, before);
      setReceipts(response.data?.receipts || []);
      setNextCursor(response.data?.nextCursor || null);
      setPageCursors((current) => current.slice(0, pageIndex));
      setPageIndex((current) => current - 1);
    } catch (error) {
      showToast(errorMessage(error, "The previous receipt page could not be loaded."), "error");
    } finally {
      setLoadingPage(false);
    }
  };

  const openReceipt = async (transactionId) => {
    try {
      const response = await api.get("/receipts/" + encodeURIComponent(transactionId));
      setSelectedReceipt(response.data);
    } catch (error) {
      showToast(errorMessage(error, "Receipt details could not be loaded."), "error");
    }
  };

  const deleteReceipt = async (receipt) => {
    if (!isReceiptEditableToday(receipt?.createdAt)) return;

    const confirmed = window.confirm(
      `Delete receipt ${receipt.transactionId}? This will restore stock and remove its correction history.`,
    );
    if (!confirmed) return;

    setDeletingTransactionId(receipt.transactionId);
    try {
      await api.delete(`/receipts/${encodeURIComponent(receipt.transactionId)}`);
      setReceipts((current) =>
        current.filter((summary) => summary.transactionId !== receipt.transactionId),
      );
      showToast("Receipt deleted and stock restored.", "success");
    } catch (error) {
      showToast(
        errorMessage(error, "The receipt could not be deleted. No changes were made."),
        "error",
      );
    } finally {
      setDeletingTransactionId(null);
    }
  };

  const beginCorrection = (receipt) => {
    setSelectedReceipt(null);
    setEditingReceipt(receipt);
  };

  const finishCorrection = (updatedReceipt) => {
    setEditingReceipt(null);
    setSelectedReceipt(updatedReceipt);
    setReceipts((current) =>
      current.map((summary) =>
        summary.transactionId === updatedReceipt.transactionId
          ? {
              ...summary,
              totalAmount: updatedReceipt.totalAmount,
              itemCount: updatedReceipt.items.length,
              corrected: true,
            }
          : summary,
      ),
    );
  };

  return (
    <div className="app-layout">
      <Sidebar
        user={user}
        onLogout={handleLogout}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((current) => !current)}
        menuButtonRef={mobileMenuButtonRef}
      />
      <main className={`main-content ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        <button
          ref={mobileMenuButtonRef}
          className="mobile-menu-btn"
          onClick={handleOpenMenu}
          aria-label="Open menu"
          aria-controls="app-sidebar"
          aria-expanded={sidebarOpen}
        >
          <span></span>
        </button>
        <WorkspaceHeader
          workspaceLabel="Cashier"
          pageLabel="Receipts"
          stationLabel={user?.role === "admin" ? "Back office" : "Front counter"}
          user={user}
          onOpenCashier={user?.role === "admin" ? () => navigate("/cashier") : undefined}
        />
        <section className="card receipt-history" aria-labelledby="receipts-title">
        <header className="receipt-history-header">
          <div>
            <p className="eyebrow">SALES RECORDS</p>
            <h1 id="receipts-title">Receipts</h1>
            <p>Find, reprint, and correct today’s checkout receipts.</p>
          </div>
          <span className="receipt-history-count" data-receipt-count aria-live="polite">
            {receipts.length} receipt{receipts.length === 1 ? "" : "s"}
          </span>
        </header>

        <section className="receipt-history-filters" aria-label="Receipt filters">
          <label>
            <span>Product or barcode</span>
            <input
              name="productSearch"
              type="search"
              value={filters.productSearch}
              onChange={updateFilter}
              placeholder="Search product or barcode"
            />
          </label>
          <label>
            <span>From date</span>
            <input name="dateFrom" type="date" value={filters.dateFrom} onChange={updateFilter} />
          </label>
          <label>
            <span>To date</span>
            <input name="dateTo" type="date" value={filters.dateTo} onChange={updateFilter} />
          </label>
          <label>
            <span>From time (MMT)</span>
            <input name="timeFrom" type="time" value={filters.timeFrom} onChange={updateFilter} />
          </label>
          <label>
            <span>To time (MMT)</span>
            <input name="timeTo" type="time" value={filters.timeTo} onChange={updateFilter} />
          </label>
          <button
            type="button"
            className="btn btn-secondary"
            data-clear-receipt-filters
            onClick={clearFilters}
          >
            Clear filters
          </button>
        </section>

        {loading ? (
          <p className="receipt-history-state" role="status">Loading receipts...</p>
        ) : receipts.length === 0 ? (
          <div className="receipt-history-state">
            <strong>No receipts found</strong>
            <p>Try a different date, time, or product search.</p>
          </div>
        ) : (
          <>
            <div className="receipt-history-table-wrap">
              <table className="receipt-history-table">
                <thead>
                  <tr>
                    <th>Receipt</th>
                    <th>Date &amp; time</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receipts.map((receipt) => (
                    <tr key={receipt.id || receipt.transactionId}>
                      <td><code>{receipt.transactionId}</code></td>
                      <td>{formatReceiptDateTime(receipt.createdAt)}</td>
                      <td>{receipt.itemCount} item(s)</td>
                      <td>MMK {formatCurrency(receipt.totalAmount)}</td>
                      <td>{receipt.corrected ? "Corrected" : "Completed"}</td>
                      <td>
                        <button
                          type="button"
                          className="table-row-action table-row-action-details"
                          data-view-receipt={receipt.transactionId}
                          onClick={() => openReceipt(receipt.transactionId)}
                        >
                          View / reprint
                        </button>
                        {isReceiptEditableToday(receipt.createdAt) && (
                          <button
                            type="button"
                            className="table-row-action table-row-action-delete"
                            data-delete-receipt={receipt.transactionId}
                            disabled={deletingTransactionId === receipt.transactionId}
                            onClick={() => deleteReceipt(receipt)}
                          >
                            {deletingTransactionId === receipt.transactionId ? "Deleting..." : "Delete"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="receipt-history-mobile-list">
              {receipts.map((receipt) => (
                <article className="receipt-history-card" key={receipt.id || receipt.transactionId}>
                  <header>
                    <code>{receipt.transactionId}</code>
                    <strong>MMK {formatCurrency(receipt.totalAmount)}</strong>
                  </header>
                  <p>{formatReceiptDateTime(receipt.createdAt)}</p>
                  <p>{receipt.itemCount} item(s) · {receipt.corrected ? "Corrected" : "Completed"}</p>
                  <button
                    type="button"
                    className="table-row-action table-row-action-details"
                    data-view-receipt={receipt.transactionId}
                    onClick={() => openReceipt(receipt.transactionId)}
                  >
                    View / reprint
                  </button>
                  {isReceiptEditableToday(receipt.createdAt) && (
                    <button
                      type="button"
                      className="table-row-action table-row-action-delete"
                      data-delete-receipt={receipt.transactionId}
                      disabled={deletingTransactionId === receipt.transactionId}
                      onClick={() => deleteReceipt(receipt)}
                    >
                      {deletingTransactionId === receipt.transactionId ? "Deleting..." : "Delete"}
                    </button>
                  )}
                </article>
              ))}
            </div>
            <nav className="receipt-history-pagination" aria-label="Receipt pagination" data-receipt-pagination>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                data-previous-receipt-page
                onClick={goToPreviousPage}
                disabled={pageIndex === 0 || loadingPage}
              >
                Previous
              </button>
              <span className="receipt-history-pagination-status" aria-live="polite">
                {loadingPage ? "Loading..." : `Page ${pageIndex + 1} · ${receipts.length} receipts`}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                data-next-receipt-page
                onClick={goToNextPage}
                disabled={!nextCursor || loadingPage}
              >
                Next
              </button>
            </nav>
          </>
        )}
        </section>

        {selectedReceipt && (
          <ReceiptDetailDialog
            receipt={selectedReceipt}
            onClose={() => setSelectedReceipt(null)}
            onPrint={() => window.print()}
            onEdit={beginCorrection}
          />
        )}

        {editingReceipt && (
          <ReceiptCorrectionDialog
            receipt={editingReceipt}
            onClose={() => setEditingReceipt(null)}
            onSaved={finishCorrection}
          />
        )}
      </main>
    </div>
  );
}
